/**
 * StateManager - Manages game state transitions
 * Handles the state machine for screens: title, playing, paused, gameOver, etc.
 */

import { LoadingScreen } from '../screens/LoadingScreen.js';
import { TitleScreen } from '../screens/TitleScreen.js';
import { PlayingState } from '../screens/PlayingState.js';
import { PauseScreen } from '../screens/PauseScreen.js';
import { GameOverScreen } from '../screens/GameOverScreen.js';
import { HighScoreScreen } from '../screens/HighScoreScreen.js';
import { SettingsScreen } from '../screens/SettingsScreen.js';
import { WaveIntroScreen } from '../screens/WaveIntroScreen.js';
import { WaveEndScreen } from '../screens/WaveEndScreen.js';

export class StateManager {
  constructor(game) {
    this.game = game;
    this.currentState = 'loading';
    this.previousState = null;

    // Initialize all screen/state handlers
    this.states = {
      loading: new LoadingScreen(game),
      title: new TitleScreen(game),
      playing: new PlayingState(game),
      paused: new PauseScreen(game),
      gameOver: new GameOverScreen(game),
      highScores: new HighScoreScreen(game),
      settings: new SettingsScreen(game),
      waveIntro: new WaveIntroScreen(game),
      waveEnd: new WaveEndScreen(game),
    };
  }

  /**
   * Transition to a new game state.
   */
  setState(newState, data = {}) {
    if (this.states[newState]) {
      this.previousState = this.currentState;

      // Exit current state
      if (this.states[this.currentState] && this.states[this.currentState].exit) {
        this.states[this.currentState].exit();
      }

      this.currentState = newState;

      // Update cursor visibility based on state
      this._updateCursorState(newState);

      // Manage ambient background audio across state transitions
      this._updateAmbientAudio(newState);

      // Enter new state
      if (this.states[this.currentState] && this.states[this.currentState].enter) {
        this.states[this.currentState].enter(data);
      }
    } else {
      console.warn(`Unknown state: ${newState}`);
    }
  }

  /**
   * Start/stop ambient background audio based on game state.
   * Ambient plays during all gameplay-related states and stops on menus.
   */
  _updateAmbientAudio(state) {
    const gameplayStates = ['playing', 'paused', 'waveIntro', 'waveEnd'];
    if (this.game.audio) {
      if (gameplayStates.includes(state)) {
        this.game.audio.startAmbient();
      } else {
        this.game.audio.stopAmbient();
      }
    }
  }

  /**
   * Toggle custom crosshair cursor based on game state.
   * During gameplay-related states, hide the browser cursor (canvas draws its own crosshair).
   */
  _updateCursorState(state) {
    const gameplayStates = ['playing', 'paused', 'waveIntro', 'waveEnd'];
    if (gameplayStates.includes(state)) {
      this.game.canvas.classList.add('gameplay-cursor');
    } else {
      this.game.canvas.classList.remove('gameplay-cursor');
    }
  }

  /**
   * Update current state.
   */
  update(dt) {
    const state = this.states[this.currentState];
    if (state && state.update) {
      state.update(dt);
    }
  }

  /**
   * Render current state.
   */
  render(ctx) {
    const state = this.states[this.currentState];
    if (state && state.render) {
      state.render(ctx);
    }
  }

  /**
   * Notify the current state about a window resize so it can reposition entities.
   * Also notifies the playing state even if paused/waveIntro/waveEnd (since they
   * render the playing state's entities underneath).
   */
  onResize(oldW, oldH, newW, newH) {
    // Always update the playing state's entities if a game is active
    const playingState = this.states.playing;
    if (playingState && playingState.onResize) {
      playingState.onResize(oldW, oldH, newW, newH);
    }
  }
}
